/* ════════════════════════════════════════════════════════════════════════
   HOME OS THEME — shared tokens for moods, rooms, energy, and family
   member colour palettes. Module tokens still live in
   /app/frontend/src/pages/homeos/data/homeOsModuleConfig.js so that file
   remains the single source of truth for navigation/icons.
   ════════════════════════════════════════════════════════════════════════ */

/* Mood meter zones (high/low energy × positive/challenging) */
export const MOOD_ZONES = {
  yellow: {
    id: "yellow", label: "High energy · positive",
    tile: "bg-amber-100 dark:bg-amber-900/30 border-amber-200 dark:border-amber-700",
    chip: "bg-amber-500 text-white hover:bg-amber-600",
    text: "text-amber-900 dark:text-amber-100",
    accent: "text-amber-700 dark:text-amber-300",
    dot: "bg-amber-500",
  },
  red: {
    id: "red", label: "High energy · challenging",
    tile: "bg-rose-100 dark:bg-rose-900/30 border-rose-200 dark:border-rose-700",
    chip: "bg-rose-500 text-white hover:bg-rose-600",
    text: "text-rose-900 dark:text-rose-100",
    accent: "text-rose-700 dark:text-rose-300",
    dot: "bg-rose-500",
  },
  green: {
    id: "green", label: "Low energy · positive",
    tile: "bg-emerald-100 dark:bg-emerald-900/30 border-emerald-200 dark:border-emerald-700",
    chip: "bg-emerald-500 text-white hover:bg-emerald-600",
    text: "text-emerald-900 dark:text-emerald-100",
    accent: "text-emerald-700 dark:text-emerald-300",
    dot: "bg-emerald-500",
  },
  blue: {
    id: "blue", label: "Low energy · challenging",
    tile: "bg-sky-100 dark:bg-sky-900/30 border-sky-200 dark:border-sky-700",
    chip: "bg-sky-500 text-white hover:bg-sky-600",
    text: "text-sky-900 dark:text-sky-100",
    accent: "text-sky-700 dark:text-sky-300",
    dot: "bg-sky-500",
  },
};

/* Mood library — short label, zone, supportive guidance for ARA noticed cards. */
export const MOODS = [
  // Yellow — high energy positive
  { id: "happy",       label: "Happy",       zone: "yellow", support: "Good time for connection." },
  { id: "excited",     label: "Excited",     zone: "yellow", support: "Channel energy into something fun." },
  { id: "proud",       label: "Proud",       zone: "yellow", support: "Celebrate the win out loud." },
  { id: "motivated",   label: "Motivated",   zone: "yellow", support: "Great time to start a task." },
  { id: "playful",     label: "Playful",     zone: "yellow", support: "Make space for play." },
  { id: "energised",   label: "Energised",   zone: "yellow", support: "Good for movement or a high-energy chore." },

  // Red — high energy challenging
  { id: "angry",       label: "Angry",       zone: "red", support: "Give space, hold off on hard asks." },
  { id: "anxious",     label: "Anxious",     zone: "red", support: "Slow down. Offer a calm presence." },
  { id: "frustrated",  label: "Frustrated",  zone: "red", support: "Avoid surprise tasks. Listen first." },
  { id: "overwhelmed", label: "Overwhelmed", zone: "red", support: "Reduce inputs. Consider Calm Mode." },
  { id: "stressed",    label: "Stressed",    zone: "red", support: "Lighten today's task load." },
  { id: "panicked",    label: "Panicked",    zone: "red", support: "Step in gently and check what's needed." },

  // Green — low energy positive
  { id: "calm",        label: "Calm",        zone: "green", support: "Protect this energy. Avoid loud surprises." },
  { id: "peaceful",    label: "Peaceful",    zone: "green", support: "Soft check-in only." },
  { id: "content",     label: "Content",     zone: "green", support: "Good for shared quiet activities." },
  { id: "grateful",    label: "Grateful",    zone: "green", support: "Notice it out loud — it spreads." },
  { id: "relaxed",     label: "Relaxed",     zone: "green", support: "Keep things gentle." },
  { id: "safe",        label: "Safe",        zone: "green", support: "Maintain calm. No surprise asks." },

  // Blue — low energy challenging
  { id: "sad",         label: "Sad",         zone: "blue", support: "Offer a quiet check-in." },
  { id: "tired",       label: "Tired",       zone: "blue", support: "Keep tasks simple. Suggest rest." },
  { id: "lonely",      label: "Lonely",      zone: "blue", support: "A small connection helps." },
  { id: "flat",        label: "Flat",        zone: "blue", support: "Lower the bar. Easy tasks only." },
  { id: "drained",     label: "Drained",     zone: "blue", support: "Pause non-essentials." },
  { id: "discouraged", label: "Discouraged", zone: "blue", support: "Validate first, then offer help." },
];

/* Simplified emotion wheel — 6 primaries × 4 secondaries each */
export const EMOTION_WHEEL = [
  { id: "happy",    label: "Happy",    zone: "yellow", children: ["Joyful", "Proud", "Excited", "Playful"] },
  { id: "sad",      label: "Sad",      zone: "blue",   children: ["Lonely", "Disappointed", "Tired", "Hurt"] },
  { id: "angry",    label: "Angry",    zone: "red",    children: ["Frustrated", "Irritated", "Annoyed", "Resentful"] },
  { id: "scared",   label: "Scared",   zone: "red",    children: ["Worried", "Anxious", "Nervous", "Unsafe"] },
  { id: "peaceful", label: "Peaceful", zone: "green",  children: ["Calm", "Content", "Relaxed", "Thankful"] },
  { id: "powerful", label: "Powerful", zone: "yellow", children: ["Confident", "Brave", "Focused", "Capable"] },
];

/* Room palette — colour-coded by space */
export const ROOMS = {
  kitchen:    { id: "kitchen",    label: "Kitchen",     tile: "bg-emerald-50 dark:bg-emerald-900/15 border-emerald-200 dark:border-emerald-800", dot: "bg-emerald-500", accent: "text-emerald-700 dark:text-emerald-300", icon: "ChefHat" },
  bathroom:   { id: "bathroom",   label: "Bathroom",    tile: "bg-sky-50 dark:bg-sky-900/15 border-sky-200 dark:border-sky-800",                   dot: "bg-sky-500",     accent: "text-sky-700 dark:text-sky-300",         icon: "Bath" },
  living:     { id: "living",     label: "Living Room", tile: "bg-amber-50 dark:bg-amber-900/15 border-amber-200 dark:border-amber-800",           dot: "bg-amber-500",   accent: "text-amber-700 dark:text-amber-300",     icon: "Sofa" },
  bedroom:    { id: "bedroom",    label: "Bedroom",     tile: "bg-violet-50 dark:bg-violet-900/15 border-violet-200 dark:border-violet-800",       dot: "bg-violet-500",  accent: "text-violet-700 dark:text-violet-300",   icon: "BedDouble" },
  laundry:    { id: "laundry",    label: "Laundry",     tile: "bg-cyan-50 dark:bg-cyan-900/15 border-cyan-200 dark:border-cyan-800",               dot: "bg-cyan-500",    accent: "text-cyan-700 dark:text-cyan-300",       icon: "Shirt" },
  entryway:   { id: "entryway",   label: "Entryway",    tile: "bg-orange-50 dark:bg-orange-900/15 border-orange-200 dark:border-orange-800",       dot: "bg-orange-500",  accent: "text-orange-700 dark:text-orange-300",   icon: "DoorOpen" },
  office:     { id: "office",     label: "Office",      tile: "bg-indigo-50 dark:bg-indigo-900/15 border-indigo-200 dark:border-indigo-800",       dot: "bg-indigo-500",   accent: "text-indigo-700 dark:text-indigo-300",   icon: "Briefcase" },
  garage:     { id: "garage",     label: "Garage",      tile: "bg-slate-50 dark:bg-slate-700/30 border-slate-200 dark:border-slate-600",           dot: "bg-slate-500",   accent: "text-slate-700 dark:text-slate-300",     icon: "Warehouse" },
  storage:    { id: "storage",    label: "Storage",     tile: "bg-stone-50 dark:bg-stone-800/40 border-stone-200 dark:border-stone-700",           dot: "bg-stone-500",   accent: "text-stone-700 dark:text-stone-300",     icon: "Package" },
  outdoor:    { id: "outdoor",    label: "Outdoor",     tile: "bg-lime-50 dark:bg-lime-900/15 border-lime-200 dark:border-lime-800",               dot: "bg-lime-500",    accent: "text-lime-700 dark:text-lime-300",       icon: "Trees" },
  playroom:   { id: "playroom",   label: "Playroom",    tile: "bg-pink-50 dark:bg-pink-900/15 border-pink-200 dark:border-pink-800",               dot: "bg-pink-500",    accent: "text-pink-700 dark:text-pink-300",       icon: "Blocks" },
};

export const ROOM_LIST = Object.values(ROOMS);

/* Energy tokens used across mood + tasks. */
export const ENERGY = {
  Low:    { label: "Low",    cls: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",       dot: "bg-sky-500" },
  Medium: { label: "Medium", cls: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300", dot: "bg-amber-500" },
  High:   { label: "High",   cls: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300", dot: "bg-emerald-500" },
};

export function moodFor(id) {
  return MOODS.find(m => m.id === id || m.label.toLowerCase() === (id || "").toLowerCase());
}

export function zoneFor(zoneId) {
  return MOOD_ZONES[zoneId] || MOOD_ZONES.green;
}
